import { Action, Dispatch } from 'redux'
import { State } from 'index'
import { isSameDay, subMinutes } from 'date-fns'
import { push } from 'connected-react-router'
import { db, FirebaseTimestamp, FirebaseWriteBatch } from 'firebase/index'
import { BreadLabel, BreadType } from 'types/bread'
import { ScheduleType } from 'types/schedule'
import { StockType } from 'types/stock'
import { StoreCalendarType } from 'types/storeCalendar'
import { StoreBreadType } from 'types/storeBread'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { sortBreads } from 'reducks/bread/operations'
import { parseToDate } from 'lib/helper'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { StoreDeliveryBreadType } from 'types/storeDeliveryBread'
const daysOfWeek = new DaysOfWeek()

const saveBreadWithSchedules = (
  bread: BreadType,
  breadLabels: BreadLabel[],
  schedules: ScheduleType[],
  storeDeliveryBread: StoreDeliveryBreadType,
  prevStocks: StockType[],
  openStoreCalendars: StoreCalendarType[],
  isEditPage: boolean
) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    if (!bread.bread_ref) return
    const categories = getState().category.categories
    const manufacturerRef = getState().auth.manufacturer_ref
    const specialHolidays = getState().calendar.specialHolidays
    const storeRef = getState().auth.store_ref
    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0
    const now = FirebaseTimestamp.now()
    dispatch(showLoadingAction())
    /**
     * breadsとstore_breadsの更新
     */
    // 新規作成であれば並び順を更新する
    if (!isEditPage) {
      bread.order = await sortBreads(bread, categories, manufacturerRef, now, storeRef)
    }
    // 商品情報を保存する
    const breadRef = bread.bread_ref ?? db.collection('breads').doc()
    delete bread.bread_ref
    bread.updated_at = now
    batchArray[batchIndex].set(breadRef, bread, { merge: true })
    operationCounter++
    if (operationCounter === 499) {
      batchArray.push(db.batch())
      batchIndex++
      operationCounter = 0
    }
    // 新規追加の場合、store_breadsに参照を作成する
    if (!isEditPage) {
      const storeBreadValue: StoreBreadType = {
        bread_ref: breadRef,
        created_at: now,
        updated_at: now,
      }
      // sacri stationの場合、生産店舗のrefを追加する
      if (typeof manufacturerRef !== 'undefined') {
        storeBreadValue.manufacturer_ref = manufacturerRef
      }
      batchArray[batchIndex].set(storeRef.collection('store_breads').doc(), storeBreadValue)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }
    // 商品紹介タグの取得と削除
    const prevBreadLabels = await breadRef
      .collection('bread_labels')
      .get()
      .then((snapshots) =>
        snapshots.docs.map((snapshot) => {
          const prevBreadLabel = snapshot.data() as BreadLabel
          // 選択された商品紹介タグに含まれていなければ削除
          if (!breadLabels.some((breadLabel) => breadLabel.label_ref.id === prevBreadLabel.label_ref.id)) {
            batchArray[batchIndex].delete(snapshot.ref)
            operationCounter++
            if (operationCounter === 499) {
              batchArray.push(db.batch())
              batchIndex++
              operationCounter = 0
            }
          }
          return prevBreadLabel
        })
      )
    // 商品紹介タグの新規追加
    for (let i = 0; i < breadLabels.length; i = (i + 1) | 0) {
      const breadLabel = breadLabels[i]
      if (!prevBreadLabels.some((prevBreadLabel) => breadLabel.label_ref.id === prevBreadLabel.label_ref.id)) {
        const breadLabelRef = breadRef.collection('bread_labels').doc()
        batchArray[batchIndex].set(breadLabelRef, breadLabel, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }
    }
    /**
     * ここからstore_schedulesとstore_stocksの更新
     */
    // 変更前のschedulesを取得
    const prevScheduleSnapshots = await storeRef.collection('store_schedules').where('bread_ref', '==', breadRef).get()
    // 変更前後のスケジュールを比較して、変更後に存在しないものは削除
    for (let i = 0; i < prevScheduleSnapshots.docs.length; i = (i + 1) | 0) {
      const prevSnapshot = prevScheduleSnapshots.docs[i]
      const prevSchedule = prevSnapshot.data() as ScheduleType
      if (
        schedules.some((nextSchedule) => {
          if (!nextSchedule.schedule_ref) return false
          return nextSchedule.schedule_ref.id === prevSnapshot.ref.id
        })
      )
        continue
      batchArray[batchIndex].delete(prevSnapshot.ref)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
      // 削除された受付設定と紐づく在庫データを削除
      const prevStocksOfType = prevStocks.filter((_stock) => _stock.type === prevSchedule.type)
      for (let i = 0; i < prevStocksOfType.length; i = (i + 1) | 0) {
        const stock = prevStocksOfType[i]
        if (!stock.stock_ref) continue
        // 在庫idから対象のscheduleか判定する
        const scheduleId = stock.stock_ref.id.split(`stock_${prevSchedule.type}_`)[1].split('-')[0]
        if (prevSnapshot.ref.id !== scheduleId) continue
        batchArray[batchIndex].delete(stock.stock_ref)
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }
    }

    const scheduleHasPreorderStartedAt = schedules.find((schedule) => schedule.preorder_started_at)

    for (let i = 0; i < schedules.length; i = (i + 1) | 0) {
      const schedule = schedules[i]
      // 参照用に持っていたrefを削除（Firestoreに保存する必要がないため）
      const scheduleRef = schedule.schedule_ref
      if (!scheduleRef) continue
      delete schedule.schedule_ref
      // choicesに空白の値があれば削除
      schedule.choices = [...schedule.choices.filter((choice) => choice !== '')]
      // sacri stationの場合、販売元店舗のrefを追加する
      if (typeof manufacturerRef !== 'undefined') {
        schedule.manufacturer_ref = manufacturerRef
      }
      // 予約販売開始日が設定されている場合、値を複製する
      if (scheduleHasPreorderStartedAt && schedule.type === 'preorder') {
        schedule.preorder_started_at = scheduleHasPreorderStartedAt.preorder_started_at
      }
      // scheduleの上書き保存
      batchArray[batchIndex].set(scheduleRef, schedule, { merge: true })
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
      // 当日以降の在庫データを更新する
      const prevStocksOfType = prevStocks.filter((_stock) => _stock.type === schedule.type)
      for (let i = 0; i < prevStocksOfType.length; i = (i + 1) | 0) {
        const stock = prevStocksOfType[i]
        if (!stock.stock_ref) continue
        // 削除扱いの在庫データはスキップ
        if (stock.is_deleted) continue
        // 在庫idから対象のscheduleか判定する
        const scheduleId = stock.stock_ref.id.split(`stock_${schedule.type}_`)[1].split('-')[0]
        if (scheduleRef.id !== scheduleId) continue
        // 当日販売中の当日取り置き在庫かどうか
        const hasDailyStockOnTheDate = !(stock.type === 'daily' && isSameDay(parseToDate(stock.date), new Date()))
        // 更新後の受渡可能日に存在しない（臨時営業日、当日取り置きの販売中/販売予定は除外）
        if (
          !schedule.days_of_week.includes(daysOfWeek.getDayOfWeek(parseToDate(stock.date), specialHolidays)) &&
          openStoreCalendars.every((storeCalendar) => storeCalendar.date !== stock.date) &&
          hasDailyStockOnTheDate
        ) {
          batchArray[batchIndex].delete(stock.stock_ref)
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
          // 更新後の受渡解禁時刻に存在しないstockを削除（当日取り置きの販売中/販売予定は除外）
        } else if (
          schedule.days_of_week.includes(daysOfWeek.getDayOfWeek(parseToDate(stock.date), specialHolidays)) &&
          schedule.default_stocks.every((defaultStock) => defaultStock.pickupable_time !== stock.pickupable_time) &&
          hasDailyStockOnTheDate
        ) {
          batchArray[batchIndex].delete(stock.stock_ref)
          operationCounter++
          if (operationCounter === 499) {
            batchArray.push(db.batch())
            batchIndex++
            operationCounter = 0
          }
          // 受渡解禁時刻の変更の場合は新規作成
          stock.bread_name = bread.name
          stock.choices = schedule.choices
          // 事前予約の場合、受付締切時刻を更新
          if (stock.type !== 'daily') {
            stock.deadline = FirebaseTimestamp.fromDate(
              subMinutes(parseToDate(stock.date), schedule.hours_to_deadline * 60)
            )
          }
          stock.image_url = bread.image_urls[0] || ''
          stock.maximum_order = schedule.maximum_order
          stock.note = schedule.note
          stock.options = schedule.options
          stock.price = bread.price
          // 非公開ステータスの場合、論理削除する
          if (bread.status === 'private') {
            stock.is_deleted = true
            stock.stock = 0
          }
          for (let j = 0; j < schedule.default_stocks.length; j = (j + 1) | 0) {
            const defaultStock = schedule.default_stocks[j]
            stock.pickupable_time = defaultStock.pickupable_time
            stock.stock = defaultStock.stock
            const stockRef = storeRef
              .collection('store_stocks')
              .doc(`stock_${stock.type}_${scheduleRef.id}-${stock.date}_${stock.pickupable_time}`)
            delete stock.stock_ref
            batchArray[batchIndex].set(stockRef, stock, { merge: true })
            operationCounter++
            if (operationCounter === 499) {
              batchArray.push(db.batch())
              batchIndex++
              operationCounter = 0
            }
          }
        } else {
          // 上書き保存
          stock.bread_name = bread.name
          stock.choices = schedule.choices
          // 事前予約の場合、受付締切時刻を更新
          if (stock.type !== 'daily') {
            stock.deadline = FirebaseTimestamp.fromDate(
              subMinutes(parseToDate(stock.date), schedule.hours_to_deadline * 60)
            )
          }
          stock.image_url = bread.image_urls[0] || ''
          stock.maximum_order = schedule.maximum_order
          stock.note = schedule.note
          stock.options = schedule.options
          stock.price = bread.price
          // 非公開ステータスの場合、論理削除する
          if (bread.status === 'private') {
            stock.is_deleted = true
            stock.stock = 0
          }
          for (let j = 0; j < schedule.default_stocks.length; j = (j + 1) | 0) {
            const defaultStock = schedule.default_stocks[j]
            // 都度配信の場合・更新後の受渡解禁時刻に存在しない場合は受渡解禁時刻を上書きしない
            if (hasDailyStockOnTheDate) {
              stock.pickupable_time = defaultStock.pickupable_time
            }
            // 当日取り置きの当日販売中/翌営業日の販売予定は在庫数を更新しない
            if (hasDailyStockOnTheDate) {
              stock.stock = defaultStock.stock
            }
            const stockRef = storeRef
              .collection('store_stocks')
              .doc(`stock_${stock.type}_${scheduleRef.id}-${stock.date}_${stock.pickupable_time}`)
            delete stock.stock_ref
            batchArray[batchIndex].set(stockRef, stock, { merge: true })
            operationCounter++
            if (operationCounter === 499) {
              batchArray.push(db.batch())
              batchIndex++
              operationCounter = 0
            }
          }
        }
      }
    }

    const storeDeliveryBreadRef = storeDeliveryBread?.ref || storeRef.collection('store_delivery_breads').doc()
    if (storeDeliveryBread.ref) {
      delete storeDeliveryBread.ref
    }
    batchArray[batchIndex].set(storeDeliveryBreadRef, storeDeliveryBread, { merge: true })
    operationCounter++
    if (operationCounter === 499) {
      batchArray.push(db.batch())
      batchIndex++
      operationCounter = 0
    }
    // 取り寄せの配送可能日(stocks)を更新
    const deliveryStocks = prevStocks.filter((stock) => stock.type === 'delivery')
    for (let i = 0; i < deliveryStocks.length; i = (i + 1) | 0) {
      const deliveryStock = deliveryStocks[i]
      if (!deliveryStock.stock_ref) continue
      // 非公開ステータスの場合、論理削除する
      if (bread.status === 'private') {
        deliveryStock.is_deleted = true
        deliveryStock.stock = 0
      }
      // 取り寄せ設定が設定されている場合
      if (bread.is_type_delivery) {
        // choicesに空白の値があれば削除
        storeDeliveryBread.choices = [...storeDeliveryBread.choices.filter((choice) => choice !== '')]
        const nextDeliveryStock: StockType = {
          ...deliveryStock,
          bread_name: bread.name,
          image_url: bread.image_urls[0] || '',
          price: bread.price,
          choices: storeDeliveryBread.choices,
          delivery_company: storeDeliveryBread.delivery_company,
          deadline: FirebaseTimestamp.fromDate(
            subMinutes(parseToDate(deliveryStock.date), storeDeliveryBread.hours_to_deadline * 60)
          ),
          frozen_delivery_type: storeDeliveryBread.frozen_delivery_type,
          how_to_eat: storeDeliveryBread.how_to_eat,
          how_to_store: storeDeliveryBread.how_to_store,
          is_frozen_delivery: storeDeliveryBread.is_frozen_delivery,
          stock: storeDeliveryBread.maximum_order,
          package_size: storeDeliveryBread.package_size,
          options: storeDeliveryBread.options,
        }
        const deliveryStockRef = deliveryStock.stock_ref
        delete deliveryStock.stock_ref
        batchArray[batchIndex].set(deliveryStockRef, nextDeliveryStock, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      } else {
        // 取り寄せ設定が設定されていない/削除された場合
        deliveryStock.stock = 0
        // 非公開ステータスの場合、論理削除する
        if (bread.status === 'private') {
          deliveryStock.is_deleted = true
        }
        const deliveryStockRef = deliveryStock.stock_ref
        delete deliveryStock.stock_ref
        batchArray[batchIndex].set(deliveryStockRef, deliveryStock, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }
    }
    // バッチ処理の実行
    try {
      await batchArray.forEach(async (batch) => await batch.commit())
      dispatch(hideLoadingAction())
      if (isEditPage) {
        dispatch(push(`/product/edit/complete/${breadRef.id}`))
      } else {
        dispatch(push(`/product/complete`))
      }
    } catch (e) {
      dispatch(hideLoadingAction())
      console.error(e)
    }
  }
}

export default saveBreadWithSchedules
